/**
 * ForceBindSkillEffect — Grit grapple-path evolution of Grapple.
 *
 * Thicker kinetic tendrils bind a target for 2 seconds (up from 1.5s).
 * Held damage multiplier increases to 1.75x. During the hold, the player
 * takes 40% less incoming damage — the grabbed body acts as structural
 * cover. Replaces Grapple on the hotbar.
 *
 * Uses a custom state machine (idle -> windup -> active -> recovery ->
 * cooldown), matching GrappleSkillEffect's architecture. The DR status
 * (`grit_force_bind_dr`) is applied on hold enter and explicitly removed
 * on hold end, target death, or interrupt — it does not rely on duration
 * expiry alone because early cancellation would leave stale DR.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see grit-spec.md § Force Bind
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface ForceBindDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    hasEffect(skillId: string, key: string): boolean;
  };
  readonly statusEffects: {
    apply(statusId: string, ...args: unknown[]): void;
    remove(statusId: string, ...args: unknown[]): void;
    has(statusId: string, ...args: unknown[]): boolean;
  };
  readonly targeting: {
    hasTargetInRange(rangeTiles: number): boolean;
    shouldFizzle(): boolean;
    isTargetAlive(): boolean;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface ForceBindConfig {
  readonly windupMs: number;
  readonly holdDurationMs: number;
  readonly recoveryMs: number;
  readonly cooldownMs: number;
  readonly baseRangeTiles: number;
  readonly baseHeldMultiplier: number;
  readonly damageReduction: number;
}

const DEFAULT_CONFIG: ForceBindConfig = {
  windupMs: 100,
  holdDurationMs: 2000,           // 2 seconds (up from Grapple's 1500)
  recoveryMs: 200,
  cooldownMs: 1200,               // medium cooldown tier
  baseRangeTiles: 0.8,            // close melee range
  baseHeldMultiplier: 1.75,       // up from Grapple's 1.5
  damageReduction: 0.4,           // 40% DR during hold
};

// ---------------------------------------------------------------------------
// Phase type
// ---------------------------------------------------------------------------

export type ForceBindPhase = 'idle' | 'windup' | 'active' | 'recovery' | 'cooldown';

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class ForceBindSkillEffect implements SkillEffect {
  readonly skillId = 'grapple_evolved';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: ForceBindDeps;
  private readonly config: ForceBindConfig;

  private _phase: ForceBindPhase = 'idle';
  private _phaseTimer = 0;
  private _complete = false;
  private _fizzled = false;

  private _resolvedRange = 0;
  private _resolvedMultiplier = 0;
  private _holdActive = false;
  private _rootApplied = false;
  private _heldApplied = false;
  private _drApplied = false;
  private _holdElapsedMs = 0;

  constructor(deps: ForceBindDeps, config: Partial<ForceBindConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get phase(): ForceBindPhase { return this._phase; }
  get holdActive(): boolean { return this._holdActive; }
  get resolvedRange(): number { return this._resolvedRange; }
  get resolvedMultiplier(): number { return this._resolvedMultiplier; }
  get rootApplied(): boolean { return this._rootApplied; }
  get heldApplied(): boolean { return this._heldApplied; }
  get drApplied(): boolean { return this._drApplied; }
  get holdElapsedMs(): number { return this._holdElapsedMs; }
  get fizzled(): boolean { return this._fizzled; }

  get cooldown(): number {
    return this._phase === 'cooldown' ? this._phaseTimer : 0;
  }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    if (this._phase !== 'idle') return false;
    const bonusRange = this.deps.resolver.getAdditiveEffect('grapple', 'rangeTiles');
    this._resolvedRange = this.config.baseRangeTiles + bonusRange;
    return this.deps.targeting.hasTargetInRange(this._resolvedRange);
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    this._complete = false;
    this._holdActive = false;
    this._rootApplied = false;
    this._heldApplied = false;
    this._drApplied = false;
    this._holdElapsedMs = 0;
    this._resolvedMultiplier = 0;
    this._fizzled = this.deps.targeting.shouldFizzle();

    // Enter windup
    this._phase = 'windup';
    this._phaseTimer = this.config.windupMs;

    return true;
  }

  deactivate(): void {
    if (this._phase === 'active') {
      this.endHold();
    }
  }

  isActive(): boolean {
    return this._phase === 'windup' || this._phase === 'active' || this._phase === 'recovery';
  }

  isComplete(): boolean {
    return this._complete;
  }

  interrupt(): void {
    if (this._phase === 'idle' || this._phase === 'cooldown') return;
    this.removeDr();
    this._holdActive = false;
    this._phase = 'idle';
    this._phaseTimer = 0;
    this._complete = true;
  }

  update(delta: number): void {
    if (this._phase === 'idle') return;

    // During hold: track elapsed time and check target liveness
    if (this._phase === 'active') {
      this._holdElapsedMs += delta;
      if (!this.deps.targeting.isTargetAlive()) {
        this.endHold();
        return;
      }
    }

    this._phaseTimer -= delta;
    if (this._phaseTimer <= 0) {
      this.advancePhase();
    }
  }

  destroy(): void {
    this.removeDr();
    this._phase = 'idle';
    this._phaseTimer = 0;
    this._holdActive = false;
  }

  // --- Internal ---

  private advancePhase(): void {
    switch (this._phase) {
      case 'windup':
        if (this._fizzled) {
          this.enterRecovery();
        } else {
          this.enterHold();
        }
        break;
      case 'active':
        this.endHold();
        break;
      case 'recovery':
        this._phase = 'cooldown';
        this._phaseTimer = this.config.cooldownMs;
        this._complete = true;
        break;
      case 'cooldown':
        this._phase = 'idle';
        this._phaseTimer = 0;
        break;
    }
  }

  private enterHold(): void {
    this._phase = 'active';
    this._phaseTimer = this.config.holdDurationMs;
    this._holdActive = true;

    // Resolve held damage multiplier
    const bonusMultiplier = this.deps.resolver.getAdditiveEffect('grapple', 'heldDamageMultiplier');
    this._resolvedMultiplier = this.config.baseHeldMultiplier + bonusMultiplier;

    // Apply statuses to target
    this.deps.statusEffects.apply('root', this.config.holdDurationMs, 'force_bind');
    this._rootApplied = true;

    this.deps.statusEffects.apply('grapple_held', this.config.holdDurationMs, this._resolvedMultiplier, 'force_bind');
    this._heldApplied = true;

    // Apply damage reduction to player
    this.deps.statusEffects.apply('grit_force_bind_dr', this.config.holdDurationMs, this.config.damageReduction, 'force_bind');
    this._drApplied = true;
  }

  private endHold(): void {
    this.removeDr();
    this._holdActive = false;
    this.enterRecovery();
  }

  private enterRecovery(): void {
    this._phase = 'recovery';
    this._phaseTimer = this.config.recoveryMs;
  }

  private removeDr(): void {
    if (this._drApplied) {
      this.deps.statusEffects.remove('grit_force_bind_dr', 'force_bind');
      this._drApplied = false;
    }
  }
}
